import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {useAuth} from '../context/AuthContext';
import {usePeriod} from '../context/PeriodContext';
import {getContractsByPeriodKey} from '../services/payrollService';
import {formatPeriodLabel} from '../utils/periodLabel';
import {buildPayrollWorkflow} from '../utils/payrollWorkflow';
import {BranchEvidenceSummary, getWorkEvidenceSummary} from '../services/workEvidenceService';

/**
 * Self-contained data source for the shared workflow-steps card (PayrollWorkflowSteps), for pages
 * that don't already load dashboard data: derives the steps for the globally selected period.
 */
export function usePayrollWorkflow() {
    const {t} = useTranslation();
    const {token} = useAuth();
    const {selectedPeriod, selectedPeriodValue, allPeriods, branches} = usePeriod();

    const [contractCount, setContractCount] = React.useState(0);
    const [evidence, setEvidence] = React.useState({total: 0, pending: 0});
    const financialsAvailable = allPeriods.find((p) => p.periodKey === selectedPeriod)?.workflowStage === 'FINALIZED';

    React.useEffect(() => {
        if (!token || !selectedPeriod) return;
        let cancelled = false;
        Promise.all([
            getContractsByPeriodKey(token, selectedPeriod).then((r) => r.contracts?.length ?? 0).catch(() => 0),
            getWorkEvidenceSummary(token, selectedPeriod).catch((): BranchEvidenceSummary[] => []),
        ]).then(([contracts, evidenceByBranch]) => {
            if (cancelled) return;
            setContractCount(contracts);
            setEvidence({
                total: evidenceByBranch.reduce((sum, r) => sum + r.total, 0),
                pending: evidenceByBranch.reduce((sum, r) => sum + r.pending, 0),
            });
        }).catch(() => {
            // Non-critical card: fail quietly and keep the last known steps.
        });
        return () => {
            cancelled = true;
        };
    }, [token, selectedPeriod, branches]);

    const periodLabel = React.useMemo(() => {
        const period = allPeriods.find((p) => p.periodKey === selectedPeriod);
        return period ? formatPeriodLabel(t, period.periodKey, period.value) : '';
    }, [allPeriods, selectedPeriod, t]);

    const workflow = buildPayrollWorkflow(t, {
        hasContracts: contractCount > 0,
        evidenceTotal: evidence.total,
        evidencePending: evidence.pending,
        financialsAvailable,
        isPeriodLocked: selectedPeriodValue?.toUpperCase().includes('LOCKED') ?? false,
    });

    return {...workflow, periodLabel, hasPeriod: !!selectedPeriod};
}
